/**
 * courseNavigationQuery
 * @description: this file contains everything needed to query courseNavigation java / graphql implementation
 * @public: withCourseNavigationV2
 * this is a graphql apollo HOC to return the api results
 * @public: courseNavigationV2ToCourseNavigationV1
 * the purpose of this is to transform the courseNavigationV2 into courseNavigationV1 response
 * this reduces the risk of breaking things.  once we are finished with the week to module experiment, we can
 * refactor the course navigation.
 * @public: type CourseNavigationItemKeys
 * a type for the results to automatically update the expected types based on the graphQL schema for available
 * CourseNavigationItems
 */
import type { CourseNavigation_DeadlineStatus, CourseNavigation_NavigationItems } from '__generated__/graphql-types';
import { CourseNavigation_ModuleType } from '__generated__/graphql-types';

import loggerSingleton from 'js/app/loggerSingleton';

import type {
  CourseNavigationV1,
  DeadlineStatus as DeadlineStatusV1,
  NavigationItem,
  NavigationItemAdminNavigationItem,
  NavigationItemClassmatesNavigationItem,
  NavigationItemCourseInfoNavigationItem,
  NavigationItemCourseManagerNavigationItem,
  NavigationItemDiscussionForumsNavigationItem,
  NavigationItemGradesNavigationItem,
  NavigationItemInboxNavigationItem,
  NavigationItemNotesNavigationItem,
  NavigationItemOfficeHoursNavigationItem,
  NavigationItemPreviewCourseNavigationItem,
  NavigationItemPreviewFirstWeekNavigationItem,
  NavigationItemResourcesNavigationItem,
  NavigationItemTeamworkNavigationItem,
  NavigationItemWeeksNavigationItem,
} from 'bundles/naptimejs/resources/__generated__/CourseNavigationV1';

// course navigation items sorted for the required ordering
const SortedNavigationItemKeysV2 = [
  'admin',
  'modules',
  'grades',
  'notes',
  'discussionForums',
  'inbox',
  'officeHours',
  'teamwork',
  'classmates',
  'resources',
  'courseInfo',
  'courseManager',
] as const;

const MODULES = 'modules';
const RESOURCES = 'resources';

/* eslint-disable camelcase */
export type CourseNavigationItemKeys = Exclude<Exclude<keyof CourseNavigation_NavigationItems, 'id'>, '__typename'>;

// Deadline status constants MAP.
// TODO: determine what invalid status should be.
const DeadlineStatusV2toV1: Record<CourseNavigation_DeadlineStatus, DeadlineStatusV1> = {
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED',
  INVALID: 'ONTRACK', // TODO log issues here
  ONTRACK: 'ONTRACK',
  OVERDUE: 'OVERDUE',
};

type ModuleNavigationItemTypes =
  | NavigationItemWeeksNavigationItem
  | NavigationItemPreviewCourseNavigationItem
  | NavigationItemPreviewFirstWeekNavigationItem;

interface KeyMapV2toV1 {
  admin: () => NavigationItemAdminNavigationItem;
  classmates: () => NavigationItemClassmatesNavigationItem;
  courseInfo: () => NavigationItemCourseInfoNavigationItem;
  courseManager: () => NavigationItemCourseManagerNavigationItem;
  discussionForums: () => NavigationItemDiscussionForumsNavigationItem;
  grades: () => NavigationItemGradesNavigationItem;
  inbox: () => NavigationItemInboxNavigationItem;
  modules: (data: NonNullable<CourseNavigation_NavigationItems[typeof MODULES]>) => ModuleNavigationItemTypes;
  notes: () => NavigationItemNotesNavigationItem;
  officeHours: () => NavigationItemOfficeHoursNavigationItem;
  resources: (
    data: NonNullable<CourseNavigation_NavigationItems[typeof RESOURCES]>
  ) => NavigationItemResourcesNavigationItem;
  teamwork: () => NavigationItemTeamworkNavigationItem;
}

// Map: Navigation Item from courseNavigationV2 item type keys to translate them to the courseNavigationV1 structure
// usage: keyMapV2toV1[navigationItem['type']](navigationItem['data'])
const keyMapV2toV1: KeyMapV2toV1 = {
  admin: () => ({
    typeName: 'adminNavigationItem',
    definition: {},
  }),
  classmates: () => ({
    typeName: 'classmatesNavigationItem',
    definition: {},
  }),
  courseInfo: () => ({
    typeName: 'courseInfoNavigationItem',
    definition: {},
  }),
  courseManager: () => ({
    typeName: 'courseManagerNavigationItem',
    definition: {},
  }),
  discussionForums: () => ({
    typeName: 'discussionForumsNavigationItem',
    definition: {},
  }),
  grades: () => ({
    typeName: 'gradesNavigationItem',
    definition: {},
  }),
  inbox: () => ({
    typeName: 'inboxNavigationItem',
    definition: { notificationCount: 0 },
  }),
  // modules are previously known as Weeks
  modules: (data) => {
    const weeks =
      data.moduleStatuses?.map(({ deadlineStatus }) => {
        if (deadlineStatus === 'INVALID') {
          // log an unexpected or unknown case.
          loggerSingleton.error(
            `deadlineStatus flagged as INVALID. treating this as ${DeadlineStatusV2toV1.ONTRACK}, but needs to be evaluated`
          );
        }
        return { weekStatus: DeadlineStatusV2toV1[deadlineStatus] };
      }) || [];

    let results: ModuleNavigationItemTypes;

    switch (data.moduleType) {
      case CourseNavigation_ModuleType.Preview:
        results = {
          typeName: 'previewCourseNavigationItem',
          definition: {
            currentWeek: 1,
            weeks,
          },
        };
        break;
      case CourseNavigation_ModuleType.FirstWeek:
        results = {
          typeName: 'previewFirstWeekNavigationItem',
          definition: {
            weeks,
          },
        };
        break;
      default:
        results = {
          typeName: 'weeksNavigationItem',
          definition: {
            currentWeekNumber: data.currentModule || -1,
            weeks,
          },
        };
    }

    return results;
  },
  notes: () => ({
    typeName: 'notesNavigationItem',
    definition: {},
  }),
  officeHours: () => ({
    typeName: 'officeHoursNavigationItem',
    definition: {},
  }),
  resources: (
    data: NonNullable<CourseNavigation_NavigationItems['resources']>
  ): NavigationItemResourcesNavigationItem => ({
    typeName: 'resourcesNavigationItem',
    definition: {
      resources: data.resources?.map(({ name, shortReferenceId: shortId }) => ({ shortId, name })) || [],
    },
  }),
  teamwork: () => ({
    typeName: 'teamworkNavigationItem',
    definition: {},
  }),
};

// generate the course navigation v1 data structure from the course navigation v2 data
export function courseNavigationV2ToCourseNavigationV1(
  V2Response: CourseNavigation_NavigationItems
): CourseNavigationV1 {
  // basic v1 response structure
  const result: CourseNavigationV1 = {
    id: V2Response.id || '',
    navigationItems: [],
  };

  // sort the course navigation items
  SortedNavigationItemKeysV2.forEach((k) => {
    let response: (typeof V2Response)[keyof typeof V2Response] = V2Response[k];
    const hasParser = response && typeof keyMapV2toV1[k] === 'function';
    if (hasParser) {
      let data: NavigationItem | undefined | null;

      // let response;
      // this switch statement exists only to satisfy types / typescript
      // I was not able to figure out how to type the keys and responses without this
      switch (k) {
        case MODULES:
          response = V2Response[MODULES];
          data = response && keyMapV2toV1[MODULES](response);
          break;
        case RESOURCES:
          response = V2Response[RESOURCES];
          data = response && keyMapV2toV1[RESOURCES](response);
          break;
        default:
          data = keyMapV2toV1[k]();
      }
      if (data) {
        result.navigationItems.push(data);
      }
    }
  });

  return result;
}
